import React from 'react'
import { cn } from '../../utils/cn'

interface ResponsiveContainerProps {
  children: React.ReactNode
  className?: string
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'container' | 'full'
  padding?: 'none' | 'sm' | 'md' | 'lg'
  as?: keyof JSX.IntrinsicElements
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  container: 'max-w-container',
  full: 'max-w-full',
}

const paddingClasses = {
  none: '',
  sm: 'px-4',
  md: 'px-4 sm:px-6',
  lg: 'px-4 sm:px-6 lg:px-8',
}

export function ResponsiveContainer({
  children,
  className,
  maxWidth = 'container',
  padding = 'lg',
  as: Component = 'div',
}: ResponsiveContainerProps) {
  return (
    <Component
      className={cn(
        'mx-auto w-full',
        maxWidthClasses[maxWidth],
        paddingClasses[padding],
        className
      )}
    >
      {children}
    </Component>
  )
}

interface ResponsiveGridProps {
  children: React.ReactNode
  className?: string
  cols?: {
    default?: number
    sm?: number
    md?: number
    lg?: number
    xl?: number
  }
  gap?: 'sm' | 'md' | 'lg' | 'xl'
  as?: keyof JSX.IntrinsicElements
}

const gapClasses = {
  sm: 'gap-4',
  md: 'gap-6',
  lg: 'gap-8',
  xl: 'gap-12',
}

export function ResponsiveGrid({
  children,
  className,
  cols = { default: 1, md: 2, lg: 3 },
  gap = 'md',
  as: Component = 'div',
}: ResponsiveGridProps) {
  const gridClasses = []
  
  if (cols.default) gridClasses.push(`grid-cols-${cols.default}`)
  if (cols.sm) gridClasses.push(`sm:grid-cols-${cols.sm}`)
  if (cols.md) gridClasses.push(`md:grid-cols-${cols.md}`)
  if (cols.lg) gridClasses.push(`lg:grid-cols-${cols.lg}`)
  if (cols.xl) gridClasses.push(`xl:grid-cols-${cols.xl}`)

  return (
    <Component
      className={cn(
        'grid',
        gapClasses[gap],
        ...gridClasses,
        className
      )}
    >
      {children}
    </Component>
  )
}

interface ResponsiveStackProps {
  children: React.ReactNode
  className?: string
  direction?: {
    default?: 'row' | 'col'
    sm?: 'row' | 'col'
    md?: 'row' | 'col'
    lg?: 'row' | 'col'
  }
  gap?: 'sm' | 'md' | 'lg' | 'xl'
  align?: 'start' | 'center' | 'end' | 'stretch'
  justify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly'
  as?: keyof JSX.IntrinsicElements
}

const alignClasses = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
}

const justifyClasses = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
}

export function ResponsiveStack({
  children,
  className,
  direction = { default: 'col', md: 'row' },
  gap = 'md',
  align = 'start',
  justify = 'start',
  as: Component = 'div',
}: ResponsiveStackProps) {
  const directionClasses = []
  
  if (direction.default) {
    directionClasses.push(direction.default === 'row' ? 'flex-row' : 'flex-col')
  }
  if (direction.sm) {
    directionClasses.push(direction.sm === 'row' ? 'sm:flex-row' : 'sm:flex-col')
  }
  if (direction.md) {
    directionClasses.push(direction.md === 'row' ? 'md:flex-row' : 'md:flex-col')
  }
  if (direction.lg) {
    directionClasses.push(direction.lg === 'row' ? 'lg:flex-row' : 'lg:flex-col')
  }

  return (
    <Component
      className={cn(
        'flex',
        gapClasses[gap],
        alignClasses[align],
        justifyClasses[justify],
        ...directionClasses,
        className
      )}
    >
      {children}
    </Component>
  )
}